import {
  Box,
  DialogBackdrop,
  DialogContent,
  DialogPositioner,
  DialogRoot,
  Flex,
  Portal,
  Text,
} from "@chakra-ui/react";
import { Icon } from "../Icon/Icon";
import { GhostButton, PrimaryButton } from "../DialogButtons/DialogButtons";
import { NotificationConsentDisclosure } from "../NotificationConsentDisclosure/NotificationConsentDisclosure";

interface EnableTextConsentDialogProps {
  isShown: boolean;
  onConfirm: () => void;
  onClose: () => void;
  loading?: boolean;
}

// Affirmative-consent confirmation shown in Settings when a user turns on text
// for a category and hasn't consented yet (or the disclosure changed). Agreeing
// captures consent once; afterward the per-category text toggles work without
// re-prompting (the server records the disclosure hash on the user).
export const EnableTextConsentDialog = ({
  isShown,
  onConfirm,
  onClose,
  loading,
}: EnableTextConsentDialogProps) => {
  return (
    <DialogRoot
      open={isShown}
      onOpenChange={(details) => {
        if (!details.open) onClose();
      }}
      size="md"
      placement="center"
      motionPreset="slide-in-bottom"
    >
      <Portal>
        <DialogBackdrop zIndex={2000} />
        <DialogPositioner zIndex={2001}>
          <DialogContent
            w={{ base: "calc(100% - 2rem)", md: "28rem" }}
            maxW="28rem"
            mx="auto"
            borderRadius="md"
            bg="bg.canvas"
            overflow="hidden"
          >
            <Flex
              align="center"
              px="5"
              py="3"
              borderBottom="1px solid"
              borderColor="border.subtle"
            >
              <Text textStyle="eyebrowBold" color="fg.muted">
                Turn on text notifications
              </Text>
            </Flex>
            <Box p="5">
              <Text textStyle="body" color="fg.default" mb="3">
                We&apos;ll text you for the categories you turn on. Agree to the
                terms below to enable text notifications.
              </Text>
              <NotificationConsentDisclosure mb="5" />
              <Flex direction={{ base: "column-reverse", sm: "row" }} gap="2">
                <GhostButton onClick={onClose} disabled={loading} flex={1}>
                  Cancel
                </GhostButton>
                <PrimaryButton onClick={onConfirm} disabled={loading} flex={2}>
                  <Icon name="check" boxSize="14px" />
                  Agree &amp; turn on texts
                </PrimaryButton>
              </Flex>
            </Box>
          </DialogContent>
        </DialogPositioner>
      </Portal>
    </DialogRoot>
  );
};
